import axios from "axios";
import api from "@flatfile/api";
import { fullAPIURL } from "../../../utils/requests";
import { RecordWithLinks, Sheet } from "@flatfile/api/api";
import {
  requestAccessTokenInterceptor,
  responseStatusInterceptor,
} from "../../../utils/axios.utils";
import { refreshAccessTokenInterceptor } from "../../../utils/refreshTokens";
import { GetPlansResult } from "../../../apis/subscriptionApi";
import {
  FetchClientSettingsDataType,
  FetchFlatFileAccessTokenDataType,
  MemberDataType,
  PaymentMigrationResponseType,
  PlanDiscountDataType,
  PostActivateMembersType,
  PostAssignPlanType,
  PostMigratingMembersPayloadType,
  PostCreateMultipleBillingRecordsType,
  PaymentMatchingFileCreateResponseType,
  PaymentMatchingVectorStoreCreateResponseType,
  PaymentMatchingVectorStoreStatusResponseType,
  UploadedUserUpdatePayload,
  PostCreateSingleBillingRecordType,
} from "../types";

const migrationsAxios = axios.create({
  baseURL: `${fullAPIURL}/v2`,
  headers: { "Content-Type": "application/json" },
});

export async function fetchFlatFileAccessToken() {
  const response =
    await migrationsAxios.get<FetchFlatFileAccessTokenDataType | null>(
      "/client/automated-migrations/edit",
    );
  return response.data;
}

export async function fetchMembersData() {
  const response = await migrationsAxios.get<MemberDataType[] | null>(
    "/client/automated-migrations",
  );
  return response.data;
}

export async function startPaymentMatching() {
  const fileId =
    await migrationsAxios.get<PaymentMatchingFileCreateResponseType>(
      "/client/automated-migrations/payment-matching/file-create",
    );
  const vectorStore =
    await migrationsAxios.post<PaymentMatchingVectorStoreCreateResponseType>(
      "/ai/vector-store",
      {
        files: [fileId.data.fileId],
        expiresAfter: 3,
      },
    );

  let status =
    await migrationsAxios.get<PaymentMatchingVectorStoreStatusResponseType>(
      `/ai/vector-store/${vectorStore.data.vectorStoreId}`,
    );

  let retries = 0;
  while (status.data.status !== "completed" && retries < 20) {
    status =
      await migrationsAxios.get<PaymentMatchingVectorStoreStatusResponseType>(
        `/ai/vector-store/${vectorStore.data.vectorStoreId}`,
      );

    if (["cancelled", "failed"].includes(status.data.status))
      throw new Error("Vector store failed to process");

    await new Promise((resolve) => setTimeout(resolve, 3000));
    retries++;
  }
  const response = await migrationsAxios.get<PaymentMigrationResponseType>(
    `/client/automated-migrations/payment-matching/${vectorStore.data.vectorStoreId}`,
  );
  return response.data;
}

export async function postMembers(payload: PostMigratingMembersPayloadType) {
  const response = await migrationsAxios.post<MemberDataType[] | null>(
    "/client/automated-migrations",
    {
      data: payload.data,
      spaceId: payload.spaceId,
    },
  );
  return response.data;
}

export async function fetchPlansDiscounts(clientUuid: string) {
  const url = `billing/${clientUuid}/discount?is_plan_discount=1`;
  const response = await migrationsAxios.get<PlanDiscountDataType>(url);
  return response.data?.data;
}

export async function postAssignPlanToMember(payload: PostAssignPlanType) {
  const response = await migrationsAxios.patch<PostAssignPlanType[]>(
    `/client/automated-migrations/${payload.id}`,
    {
      planUuid: payload.planUuid,
      planDiscountUuid: payload.planDiscountUuid,
      planStartBilling: payload.planStartBilling,
      planBillingType: payload.planBillingType,
      needPlan: payload.needPlan,
      planRemainingSessions: payload.planRemainingSessions,
      planEndDate: payload.planEndDate,
    },
  );
  return response.data;
}

// Remove non-async after async is fully implemented and working
export async function activateMembers(
  payload: PostActivateMembersType,
  enableAsyncActivation: boolean,
) {
  const URL = enableAsyncActivation
    ? "/client/automated-migrations/activate/async"
    : "/client/automated-migrations/activate";
  const response = await migrationsAxios.post<MemberDataType[]>(URL, {
    data: payload.data,
  });
  return response;
}

export async function fetchClientInfo(clientUuid: string) {
  const response =
    await migrationsAxios.get<FetchClientSettingsDataType | null>(
      `/client/client/${clientUuid}`,
    );
  return response.data;
}

export async function fetchActivePlansInfo(clientUuid: string) {
  const response = await migrationsAxios.get<GetPlansResult | null>(
    `/subscription/${clientUuid}/plan?limit=1000&active=1&deleted=0`,
  );
  return response.data;
}

export async function postCreateMultipleBillingRecords(
  payload: PostCreateMultipleBillingRecordsType,
) {
  const response = await migrationsAxios.post(
    "/client/automated-migrations/create-billing-record/batch",
    payload,
  );
  return response.data;
}

export async function postCreateSingleBillingRecord(payload: PostCreateSingleBillingRecordType) {
  const response = await migrationsAxios.post(
    "/client/automated-migrations/create-billing-record",
    payload,
  );
  return response.data;
}

export async function postRejectPaymentMethod(userId: number) {
  const response = await migrationsAxios.post(
    "/client/automated-migrations/reject-billing",
    {
      userId,
    },
  );
  return response.data;
}

export async function postSendEmailToMembers(payload: number[]) {
  const response = await migrationsAxios.post(
    "/client/automated-migrations/send-email",
    {
      userIds: payload,
    },
  );
  return response.data;
}

export const fetchSheetRecords = async (
  sheets: Sheet[],
): Promise<RecordWithLinks[]> => {
  const records = [];
  for (const [, sheet] of sheets.entries()) {
    const result = await api.records.get(sheet.id);
    records.push(...result.data.records);
  }
  return records;
};

export async function updateUploadedUser(userId: number, payload: UploadedUserUpdatePayload) {
  const response = await migrationsAxios.patch(`/client/automated-migrations/user/${userId}`, payload);
  return response.data;
}

export async function updateStripeId(userId: number, stripeId: string) {
  const matchState = !stripeId || stripeId.trim() === '' ? "unmatched" : "pending";
  const response = await updateUploadedUser(userId, { stripeId, matchState });
  return response.data;
}

migrationsAxios.interceptors.request.use(requestAccessTokenInterceptor);
migrationsAxios.interceptors.request.use(refreshAccessTokenInterceptor);
migrationsAxios.interceptors.response.use(responseStatusInterceptor);
